import React, {useState} from 'react'
import {
    Text,
    useStyleConfig,
    Stack,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import LocaleSelector from '../../components/locale-selector'
import {useIntl} from 'react-intl'
import {Link, useLocation} from 'react-router-dom'

import {getPathWithLocale} from '../../utils/url'
import useMultiSite from '../../hooks/use-multi-site'
import useShipToCountry from '../../hooks/use-ship-to-country'
import colors from '../../theme/foundations/colors'
import {PageContext, Component} from '@salesforce/commerce-sdk-react/components'
import {PDCFooterPaymentIcon} from '../../page-designer/assets'
import {PDCFooterPayments} from '../../page-designer/layouts'
import {usePage} from '@salesforce/commerce-sdk-react'

// Utils
import {getEnvStaleTime, getPageDesignerRegion} from '../../utils/utils'
import {getLocaleByReference} from '@app/utils/site-utils'
import useSiteType from '@app/hooks/use-site-type'

import PropTypes from 'prop-types'
import {OUTLET_DISPATCH_URL} from '../../constants'

const PAGEDESIGNER_TO_COMPONENT_PAYMENTS = {
    'commerce_assets.pdcFooterPaymentIcon': PDCFooterPaymentIcon,
    'commerce_layouts.pdcFooterPayments': PDCFooterPayments
}
/**
 * FooterBottom component renders the bottom section of the footer with optional minimal styling.
 * It includes locale selection, shipping country display, and payment components based on the current page context.
 *
 * @param {Object} props - The properties object.
 * @param {boolean} [props.isMinimal=false] - Determines if the footer should be rendered in a minimal style.
 * @returns {JSX.Element} The rendered footer bottom component.
 *
 * @example
 * // Usage in a React component
 * <FooterBottom isMinimal={true} />
 *
 * @description
 * The FooterBottom component utilizes various hooks and context providers to dynamically render
 * content based on the current site, locale, and page context. It supports multi-site configurations
 * and adapts its layout for checkout pages by displaying payment components when applicable.
 * The component also provides a locale selector for changing the site's language and updates the URL
 * accordingly.
 */
const FooterBottom = ({isMinimal = false}) => {
    const {isOutlet} = useSiteType()
    const styles = useStyleConfig('Footer')
    const intl = useIntl()
    const {site, buildUrl, locale, setLocale} = useMultiSite()
    const {l10n} = site
    const supportedLocales = l10n?.supportedLocales
    const {shipToCountry} = useShipToCountry()
    const location = useLocation()
    const isCheckout = /\/checkout$/.test(location?.pathname)
    const [contextValuePayments] = useState({
        components: PAGEDESIGNER_TO_COMPONENT_PAYMENTS
    })
    const staleTime = getEnvStaleTime()
    const {data: footerPage, isLoading} = usePage(
        {parameters: {pageId: 'footer' + (isOutlet ? '-outlet' : '')}},
        {staleTime: staleTime}
    )

    const footerPaymentsComponent = isLoading
        ? null
        : getPageDesignerRegion(footerPage, 'main')?.components?.find(
              (comp) => comp?.typeId === 'commerce_layouts.pdcFooterPayments'
          )

    return (
        <Box px={{base: '10px', lg: '20px'}}>
            <Stack
                direction={isMinimal ? 'row-reverse' : {base: 'column', lg: 'row-reverse'}}
                justifyContent={'space-between'}
                alignItems={{base: '', lg: 'center'}}
                borderTop={{base: '', lg: `1px solid ${colors.borderGray}`}}
                marginTop={{base: '', lg: isMinimal ? '' : '44px'}}
                paddingY={{base: '', lg: '12px'}}
            >
                {isCheckout && footerPaymentsComponent ? (
                    <PageContext.Provider value={contextValuePayments}>
                        <Component component={footerPaymentsComponent} />
                    </PageContext.Provider>
                ) : (
                    <Stack
                        direction={'row'}
                        justifyContent={'space-between'}
                        alignItems={{base: 'center', lg: 'flex-start'}}
                    >
                        {!isMinimal && !isCheckout && (
                            <Link
                                {...styles.footerLink}
                                to={isOutlet ? buildUrl(OUTLET_DISPATCH_URL) : '/'}
                                textDecoration="none"
                            >
                                <Text
                                    variant={'bodyBase1'}
                                    paddingTop={{base: '12px', lg: '12px'}}
                                    lineHeight={'24px'}
                                    alignSelf={'flex-start'}
                                >
                                    {`${intl.formatMessage({
                                        id: 'footer.shipto',
                                        defaultMessage: 'Ship to'
                                    })}`}
                                    {': '}
                                    <Text
                                        as={'span'}
                                        textTransform={'uppercase'}
                                        textDecoration={'underline'}
                                    >
                                        {shipToCountry.id}
                                    </Text>
                                </Text>
                            </Link>
                        )}
                        {!isCheckout && (
                            <LocaleSelector
                                {...styles.localeSelector}
                                selectedLocale={locale?.id}
                                locales={supportedLocales}
                                onSelect={(newLocale) => {
                                    if (isMinimal && !isOutlet) {
                                        setLocale(getLocaleByReference(site, newLocale))
                                        setTimeout(() => {
                                            window.scrollTo({
                                                top: 0,
                                                behavior: 'smooth'
                                            })
                                        }, 200)
                                    } else {
                                        const newUrl = getPathWithLocale(newLocale, buildUrl, {
                                            disallowParams: ['refine']
                                        })
                                        window.location = newUrl
                                    }
                                }}
                            />
                        )}
                    </Stack>
                )}
                <Text
                    variant={'bodyBase1'}
                    textTransform={'uppercase'}
                    alignSelf={'flex-start'}
                    lineHeight={'24px'}
                    paddingTop={isMinimal ? '12px' : {base: '', lg: '12px'}}
                    paddingBottom={
                        isMinimal ? '12px' : {base: isCheckout ? '40px' : '24px', lg: '12px'}
                    }
                >
                    {intl.formatMessage(
                        {
                            id: 'footer.brand',
                            defaultMessage: 'vilebrequin©{year}'
                        },
                        {
                            year: new Date().getFullYear()
                        }
                    )}
                </Text>
            </Stack>
        </Box>
    )
}

export default FooterBottom

FooterBottom.propTypes = {
    isMinimal: PropTypes.bool
}
